import { Folder, Loader2, Paperclip, Plus, Trash2 } from 'lucide-react';
import type { Account } from '@/api/commercial/accounts';
import type { CreditAnalysis } from '@/api/commercial/credit-analysis';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { AccountType } from '@/hooks/commercial/use-credit-analysis-state-machine';
import { useDocumentUploadForm } from './use-document-upload-form';

export interface DocumentUploadFormProps {
  proposalId: string;
  account: Account | null;
  accountType: AccountType;
  creditAnalysis: CreditAnalysis | null;
  requiredDocuments?: string[];
  declinedDocuments?: string[];
  onSuccess?: (creditAnalysis: CreditAnalysis) => void;
  onCancel?: () => void;
}

export function DocumentUploadForm({
  proposalId,
  account,
  accountType,
  creditAnalysis,
  requiredDocuments = [],
  declinedDocuments = [],
  onSuccess,
  onCancel,
}: DocumentUploadFormProps) {
  const {
    files,
    uploadProgress,
    uploadedFiles,
    isUploading,
    partnersDirectors,
    derivedAccountType,
    currentFields,
    complementaryFields,
    accountTypeLabels,
    handleFileChange,
    handlePartnerDirectorChange,
    addPartnerDirector,
    removePartnerDirector,
    handleSubmit,
    getFileExtension,
  } = useDocumentUploadForm({
    proposalId,
    account,
    accountType,
    creditAnalysis,
    onSuccess,
  });

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      {/* Account Type Display */}
      <div className="space-y-1">
        <Label htmlFor="accountType">Porte</Label>
        <p className="text-sm text-muted-foreground">{accountTypeLabels[derivedAccountType]}</p>
      </div>

      {/* Main Documents */}
      <div className="space-y-3">
        {currentFields.map((field) => {
          const progress = uploadProgress[field.key];
          const fileInfo = uploadedFiles[field.key];
          const file = files[field.key];

          return (
            <div key={field.key} className="space-y-1">
              <div className="flex items-center justify-between gap-4 px-4 py-3 border border-input rounded-md bg-background">
                <Label htmlFor={field.key} className="flex-1 cursor-pointer">
                  {field.label}
                  {field.required && <span className="text-destructive ml-1">*</span>}
                </Label>
                {!fileInfo && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 shrink-0"
                    type="button"
                    onClick={() => {
                      document.getElementById(field.key)?.click();
                    }}
                    disabled={isUploading}
                  >
                    <Paperclip className="h-4 w-4" />
                    Anexar
                  </Button>
                )}
                <input
                  type="file"
                  id={field.key}
                  name={field.key}
                  required={field.required}
                  onChange={(e) => {
                    const selectedFile = e.target.files?.[0] || null;
                    handleFileChange(field.key, selectedFile);
                  }}
                  className="hidden"
                  accept={field.accept}
                  disabled={isUploading}
                />
              </div>

              {/* Upload Progress */}
              {isUploading && progress !== undefined && progress > 0 && progress < 100 && (
                <div className="mt-2">
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Selected file display */}
              {file && !fileInfo && (
                <div className="mt-2 flex items-center gap-3 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-sky-100 text-sky-700">
                    <Folder className="h-5 w-5" />
                  </div>
                  <div className="flex flex-col flex-1">
                    <span className="font-medium text-foreground">{field.label}</span>
                    <span className="text-sm text-muted-foreground">
                      {file.name}
                      {getFileExtension(file.name) ? ` • ${getFileExtension(file.name)}` : ''}
                    </span>
                  </div>
                  {!isUploading && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleFileChange(field.key, null)}
                      className="h-8 w-8 text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}

              {/* Successfully uploaded */}
              {fileInfo && (
                <div className="mt-2 flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-green-100 text-green-700">
                    <Folder className="h-5 w-5" />
                  </div>
                  <div className="flex flex-col flex-1">
                    <span className="font-medium text-foreground">{field.label}</span>
                    <span className="text-sm text-green-700">Documento enviado com sucesso ✓</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Partners/Directors Section (for ME) */}
      {derivedAccountType === 'ME' && (
        <>
          <div className="space-y-4 mt-6">
            <h3 className="text-lg font-semibold">Sócio/Diretor</h3>
            {partnersDirectors.map((partner, index) => {
              const partnerFieldKey = `partner-doc-${partner.id}`;
              const progress = uploadProgress[partnerFieldKey];

              return (
                <div
                  key={partner.id}
                  className="space-y-3 p-4 border border-input rounded-md bg-muted/30"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`partner-name-${partner.id}`}>
                        Nome do Sócio/Diretor
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <Input
                        id={`partner-name-${partner.id}`}
                        value={partner.name}
                        onChange={(e) =>
                          handlePartnerDirectorChange(partner.id, 'name', e.target.value)
                        }
                        required
                        disabled={isUploading}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`partner-cpf-${partner.id}`}>
                        CPF
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <Input
                        id={`partner-cpf-${partner.id}`}
                        value={partner.cpf}
                        onChange={(e) =>
                          handlePartnerDirectorChange(partner.id, 'cpf', e.target.value)
                        }
                        required
                        disabled={isUploading}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between gap-4 px-4 py-3 border border-input rounded-md bg-background">
                      <Label
                        htmlFor={`partner-doc-${partner.id}`}
                        className="flex-1 cursor-pointer"
                      >
                        Documento de identificação (RG/CIN/CNH/Passaporte)
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2 shrink-0"
                        type="button"
                        onClick={() => {
                          document.getElementById(`partner-doc-${partner.id}`)?.click();
                        }}
                        disabled={isUploading}
                      >
                        <Paperclip className="h-4 w-4" />
                        Anexar
                      </Button>
                      <input
                        type="file"
                        id={`partner-doc-${partner.id}`}
                        required
                        onChange={(e) => {
                          const selectedFile = e.target.files?.[0] || null;
                          handlePartnerDirectorChange(partner.id, 'document', selectedFile);
                        }}
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        disabled={isUploading}
                      />
                    </div>

                    {/* Upload Progress for partner doc */}
                    {isUploading && progress !== undefined && progress > 0 && progress < 100 && (
                      <div className="mt-2">
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Partner doc selected */}
                    {partner.document && !uploadedFiles[partnerFieldKey] && (
                      <div className="mt-2 flex items-center gap-3 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 shadow-sm">
                        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-sky-100 text-sky-700">
                          <Folder className="h-5 w-5" />
                        </div>
                        <div className="flex flex-col flex-1">
                          <span className="font-medium text-foreground">
                            Documento: {partner.name}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {partner.document.name}
                            {getFileExtension(partner.document.name)
                              ? ` • ${getFileExtension(partner.document.name)}`
                              : ''}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {index === partnersDirectors.length - 1 && (
                    <div className="flex gap-2 justify-end">
                      {partnersDirectors.length > 1 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          type="button"
                          onClick={() => removePartnerDirector(partner.id)}
                          disabled={isUploading}
                        >
                          <Trash2 className="h-4 w-4" />
                          Remover
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        type="button"
                        onClick={addPartnerDirector}
                        disabled={isUploading}
                      >
                        <Plus className="h-4 w-4" />
                        Adicionar Sócio/Diretor
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Complementary Documents (for ME) */}
          <div className="space-y-4 mt-6">
            <h3 className="text-lg font-semibold">Documentos Complementares</h3>
            <div className="space-y-3">
              {complementaryFields.map((field) => {
                const progress = uploadProgress[field.key];
                const fileInfo = uploadedFiles[field.key];
                const file = files[field.key];

                return (
                  <div key={field.key} className="space-y-1">
                    <div className="flex items-center justify-between gap-4 px-4 py-3 border border-input rounded-md bg-background">
                      <Label htmlFor={field.key} className="flex-1 cursor-pointer">
                        {field.label}
                        {field.required && <span className="text-destructive ml-1">*</span>}
                      </Label>
                      {!fileInfo && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2 shrink-0"
                          type="button"
                          onClick={() => {
                            document.getElementById(field.key)?.click();
                          }}
                          disabled={isUploading}
                        >
                          <Paperclip className="h-4 w-4" />
                          Anexar
                        </Button>
                      )}
                      <input
                        type="file"
                        id={field.key}
                        name={field.key}
                        required={field.required}
                        onChange={(e) => {
                          const selectedFile = e.target.files?.[0] || null;
                          handleFileChange(field.key, selectedFile);
                        }}
                        className="hidden"
                        accept={field.accept}
                        disabled={isUploading}
                      />
                    </div>

                    {/* Upload Progress */}
                    {isUploading && progress !== undefined && progress > 0 && progress < 100 && (
                      <div className="mt-2">
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* File selected */}
                    {file && !fileInfo && (
                      <div className="mt-2 flex items-center gap-3 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 shadow-sm">
                        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-sky-100 text-sky-700">
                          <Folder className="h-5 w-5" />
                        </div>
                        <div className="flex flex-col flex-1">
                          <span className="font-medium text-foreground">{field.label}</span>
                          <span className="text-sm text-muted-foreground">
                            {file.name}
                            {getFileExtension(file.name) ? ` • ${getFileExtension(file.name)}` : ''}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Successfully uploaded */}
                    {fileInfo && (
                      <div className="mt-2 flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 shadow-sm">
                        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-green-100 text-green-700">
                          <Folder className="h-5 w-5" />
                        </div>
                        <div className="flex flex-col flex-1">
                          <span className="font-medium text-foreground">{field.label}</span>
                          <span className="text-sm text-green-700">
                            Documento enviado com sucesso ✓
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={isUploading}>
            Cancelar
          </Button>
        )}
        <Button type="submit" disabled={isUploading} className="gap-2">
          {isUploading && <Loader2 className="h-4 w-4 animate-spin" />}
          {isUploading ? 'Enviando...' : 'Enviar documentos'}
        </Button>
      </div>
    </form>
  );
}
